import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, RotateCw, Play, Pause, SkipBack, SkipForward,
  Eye, EyeOff, ClipboardPlus, List, Monitor, Smartphone,
} from "lucide-react";

import { findScenario } from "./scenarios";
import { getWorkflow } from "./workflow/scripts";
import DemoWorkflow from "./workflow/DemoWorkflow";
import {
  ChannelChat, DemoBadge, ChatFocusPanel,
  NotepadPanel, DraftPanel, BookingPanel,
  ConfirmationSentPanel, CustomerConfirmationPanel, ConfirmedPanel,
  VehiclePanel, ConflictPanel, WarningPanel,
  FollowUpPanel, IncidentPanel, ActivityPanel, HandoverPanel,
} from "./DemoPanels";

const AUTO_PLAY_BASE_MS = 2500;
const AUTO_PLAY_LONG_KINDS = new Set([
  "chat-focus", "notepad", "customer-confirmation", "conflict", "warning", "incident",
]);
const STORAGE_KEY = "confirmph.promoter.demo.state.v1";

function loadPersisted() {
  try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null") || null; }
  catch { return null; }
}
function persist(state) {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); }
  catch { /* private mode etc: ignore */ }
}
function clearPersisted() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

export default function DemoStage({ audience = "promoter", ownerExplainers = {}, embedded = false }) {
  const { scenarioId } = useParams();
  const navigate = useNavigate();
  const baseScenario = useMemo(() => findScenario(scenarioId), [scenarioId]);
  const workflow = useMemo(() => getWorkflow(scenarioId), [scenarioId]);
  const scenario = useMemo(
    () => (baseScenario ? { ...baseScenario, workflow } : null),
    [baseScenario, workflow]
  );
  const isWorkflow = !!workflow;

  const persisted = useRef(loadPersisted()).current;
  const initialStep = persisted && persisted.scenarioId === scenarioId ? (persisted.step || 0) : 0;

  const [step, setStep] = useState(initialStep);
  const [managerView, setManagerView] = useState(persisted?.managerView || false);
  // Homepage embeds are guided, not autoplaying slideshows. The visitor
  // completes the highlighted product action; that completion advances the
  // outer walkthrough. Standalone demo autoplay remains opt-in as before.
  const [autoPlay, setAutoPlay] = useState(false);
  const [customerConfirmed, setCustomerConfirmed] = useState(false);
  const [resetToken, setResetToken] = useState(0);
  // Device toggle: switches the Business-side surface between the office
  // computer ("desktop") and the mobile PWA ("phone").  The customer-side
  // channel visuals always stay on a phone shell (that's the customer's
  // real device).  We default to whatever the scenario prefers, but the
  // promoter can override, and the choice sticks through switches.
  const defaultDevice = workflow?.defaultDevice
                          || (baseScenario?.category === "intake" && workflow?.channel ? "phone"
                              : "desktop");
  const persistedDevice = persisted && persisted.scenarioId === scenarioId
                            ? persisted.deviceMode : null;
  const [deviceMode, setDeviceMode] = useState(persistedDevice || defaultDevice);
  const autoRef = useRef(null);

  const panels = useMemo(() => scenario?.panels || [], [scenario]);
  const current = panels[step];

  /* --------- persistence --------- */
  useEffect(() => {
    if (scenario) persist({
      scenarioId, step, managerView, deviceMode, at: Date.now(),
    });
  }, [scenarioId, scenario, step, managerView, deviceMode]);

  /* --------- auto play --------- */
  const advance = useCallback(() => {
    setStep(s => Math.min(s + 1, panels.length - 1));
  }, [panels.length]);

  useEffect(() => {
    if (!autoPlay) return;
    const kind = panels[step]?.kind;
    const delay = AUTO_PLAY_LONG_KINDS.has(kind) ? AUTO_PLAY_BASE_MS + 1200 : AUTO_PLAY_BASE_MS;
    // The last panel gets the SAME viewing duration as any other before
    // completing: scheduled here rather than firing instantly, so a
    // single-panel scenario doesn't complete before the visitor has had a
    // chance to see it.
    autoRef.current = setTimeout(() => {
      if (step >= panels.length - 1) {
        setAutoPlay(false);
        // Tells the homepage walkthrough's outer shell this scenario is
        // done: see the matching listener in BusinessProductWalkthrough.jsx.
        // Only fires for genuine panel-mode scenarios (panels.length > 0);
        // workflow-mode's own completion signal lives in the onComplete prop.
        if (embedded && panels.length > 0) {
          window.parent.postMessage({ type: "CONFIRMPH_DEMO_COMPLETE", scenarioId }, window.location.origin);
        }
      } else {
        advance();
      }
    }, delay);
    return () => clearTimeout(autoRef.current);
  }, [autoPlay, step, panels, advance, embedded, scenarioId]);

  const stopAuto = useCallback(() => setAutoPlay(false), []);

  /* --------- controls --------- */
  const reset = () => { setStep(0); setCustomerConfirmed(false); stopAuto(); setResetToken(t => t + 1); };
  const back = () => { stopAuto(); setStep(s => Math.max(0, s - 1)); };
  const next = () => { stopAuto(); advance(); };
  const actOnEmbeddedPanel = () => {
    stopAuto();
    if (step < panels.length - 1) {
      advance();
      return;
    }
    window.parent.postMessage({ type: "CONFIRMPH_DEMO_COMPLETE", scenarioId }, window.location.origin);
  };
  const chooseAnother = () => {
    clearPersisted();
    navigate(audience === "public" ? "/demo-business/scenarios" : "/promoter/demos");
  };

  if (!scenario) {
    return (
      <div className="p-6 text-center" data-testid="demo-not-found">
        <div className="text-slate-700 mb-2">Scenario not found.</div>
        <Link to="/promoter/demos" className="text-emerald-800 underline">Back to demos</Link>
      </div>
    );
  }

  const hasChannel = !!scenario.channel;
  const hint = current?.hint;

  return (
    <div className="pb-6" data-testid="demo-stage">
      {/* Top bar: hidden entirely when embedded (the homepage walkthrough
          supplies its own title/context in its own outer shell). */}
      <div className={embedded ? "hidden" : "flex items-start justify-between gap-2 flex-wrap"}>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={chooseAnother}
                    data-testid="demo-choose-another"
                    className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900">
              <List className="w-3.5 h-3.5" /> All demos
            </button>
            <DemoBadge>DEMO / SIMULATION</DemoBadge>
            {(scenario.channel?.business || scenario.business) && (
              <span className="text-[11px] text-slate-500">
                {scenario.channel?.business || scenario.business} · fictional
              </span>
            )}
          </div>
          <h1 className="mt-1 font-display text-xl sm:text-2xl font-semibold text-slate-900 truncate">
            {scenario.title}
          </h1>
          <p className="text-xs text-slate-600 mt-0.5">{scenario.subtitle}</p>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          {isWorkflow && workflow?.supportsDeviceToggle !== false && (
            <DeviceToggle mode={deviceMode} setMode={setDeviceMode} />
          )}
          {audience === "promoter" && (
            <ManagerViewToggle managerView={managerView} setManagerView={setManagerView} />
          )}
          <button onClick={reset} data-testid="demo-reset"
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full border border-slate-300 bg-white text-slate-700">
            <RotateCw className="w-3.5 h-3.5" /> Reset
          </button>
        </div>
      </div>

      {/* Progress dots: only meaningful in step-panel mode, hidden when embedded */}
      {!isWorkflow && !embedded && (
        <div className="mt-3 flex items-center gap-1.5" data-testid="demo-progress">
          {panels.map((p, i) => (
            <button key={p.key || i} onClick={() => { stopAuto(); setStep(i); }}
                    data-testid={`demo-step-dot-${i}`}
                    className={`h-1.5 rounded-full transition-all ${i === step ? "bg-[#1B4332] w-6" : i < step ? "bg-emerald-400 w-3" : "bg-slate-300 w-3"}`}
                    aria-label={`Go to step ${i + 1}`} />
          ))}
          <div className="ml-1 text-[11px] text-slate-500">{step + 1} / {panels.length}</div>
        </div>
      )}

      {/* Body */}
      {isWorkflow ? (
        <div className="mt-4" data-testid="demo-workflow-mode">
          <DemoWorkflow scenario={scenario} managerView={managerView}
                          resetToken={resetToken} autoPlay={autoPlay}
                          deviceMode={deviceMode}
                          audience={audience} embedded={embedded}
                          onAutoPlayEnd={stopAuto}
                          onComplete={() => {
                            // Real completion signal: fires when the visitor
                            // actually clicks/completes the real interaction
                            // (booking confirmed, deposit settled, replacement
                            // assigned, or CustomWorkflow's own Finish click,
                            // all of which already call this). Tells the
                            // homepage walkthrough's outer shell to move on -
                            // see the matching listener in
                            // BusinessProductWalkthrough.jsx.
                            if (embedded) {
                              window.parent.postMessage({ type: "CONFIRMPH_DEMO_COMPLETE", scenarioId }, window.location.origin);
                            }
                          }} />
        </div>
      ) : (
        <div className={`mt-4 grid gap-4 ${hasChannel ? "lg:grid-cols-2" : ""}`}>
          {hasChannel && (
            <div>
              <ChannelChat channel={scenario.channel} messages={scenario.chat || []} live runId={resetToken} fast={embedded} />
            </div>
          )}
          <div>
            {renderPanel(current, {
              onCustomerConfirm: () => { setCustomerConfirmed(true); advance(); },
            })}
            {embedded && current?.actionLabel && (
              <button
                type="button"
                onClick={actOnEmbeddedPanel}
                data-testid="panel-primary-action"
                className="mt-3 w-full rounded-lg bg-[#1B4332] px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2"
              >
                {current.actionLabel} <ChevronRight className="ml-1 inline h-4 w-4" aria-hidden="true" />
              </button>
            )}
            {!managerView && hint && (
              <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700"
                   data-testid="demo-hint">
                <div className="uppercase tracking-wider text-[10px] font-semibold text-slate-500">Promoter hint</div>
                <div className="mt-0.5">{hint}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sales notes (promoter-only, hidden in Manager View + public audience) */}
      {audience === "promoter" && !managerView && !isWorkflow && scenario.salesNotes?.length > 0 && (
        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3"
             data-testid="demo-sales-notes">
          <div className="uppercase tracking-wider text-[10px] font-semibold text-emerald-800">What to explain</div>
          <ul className="mt-1.5 space-y-1 text-sm text-slate-800 list-disc pl-5">
            {scenario.salesNotes.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </div>
      )}
      {audience === "promoter" && !managerView && isWorkflow && scenario.salesNotes?.length > 0 && (
        <details className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3"
                  data-testid="demo-sales-notes">
          <summary className="cursor-pointer text-[11px] uppercase tracking-wider font-semibold text-emerald-800">What to explain</summary>
          <ul className="mt-1.5 space-y-1 text-sm text-slate-800 list-disc pl-5">
            {scenario.salesNotes.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </details>
      )}

      {/* Business-owner explainer (public audience only, product-truth wording): hidden when embedded */}
      {audience === "public" && !embedded && ownerExplainers[scenarioId] && (
        <OwnerExplainer explainer={ownerExplainers[scenarioId]} />
      )}

      {/* Controls: panel-mode has nothing real to click (it's read-only
          summary panels), so Back/Next here are demo-only pagination
          chrome, not real app buttons. Panels auto-play through on their
          own when embedded (see the autoPlay effect above), so this whole
          row is hidden: no button for a visitor to press. */}
      {!isWorkflow && (
        <div className={embedded ? "hidden" : "mt-4 flex flex-wrap items-center justify-between gap-2"}>
          <div className="flex items-center gap-1.5">
            <button onClick={back} disabled={step === 0}
                    data-testid="demo-back"
                    className="inline-flex items-center gap-1 text-sm px-3 py-2 rounded-full border border-slate-300 bg-white disabled:opacity-40">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <button onClick={next} disabled={step >= panels.length - 1}
                    data-testid="demo-next"
                    className="inline-flex items-center gap-1 text-sm px-4 py-2 rounded-full bg-[#1B4332] text-white font-medium disabled:opacity-40">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className={embedded ? "hidden" : "flex items-center gap-1.5"}>
            {step >= panels.length - 1 && (
              <Link to="/promoter/new-log" data-testid="demo-log-visit"
                    className="inline-flex items-center gap-1 text-sm px-3 py-2 rounded-full bg-emerald-700 text-white font-medium">
                <ClipboardPlus className="w-4 h-4" /> Done: log visit
              </Link>
            )}
            <AutoPlayControls autoPlay={autoPlay} setAutoPlay={setAutoPlay}
                                onBack={back} onNext={next} onReset={reset} />
          </div>
        </div>
      )}

      {isWorkflow && !embedded && (
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
          <button onClick={() => setAutoPlay(v => !v)}
                  data-testid="demo-auto-play-toggle"
                  className={`inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full border ${autoPlay ? "bg-[#1B4332] text-white border-[#1B4332]" : "bg-white text-slate-700 border-slate-300"}`}
                  title={autoPlay ? "Pause auto-play" : "Auto play the intro"}>
            {autoPlay ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            {autoPlay ? "Pause" : "Auto play"}
          </button>
          {audience === "promoter" && (
            <Link to="/promoter/new-log" data-testid="demo-log-visit"
                  className="inline-flex items-center gap-1 text-sm px-3 py-2 rounded-full bg-emerald-700 text-white font-medium">
              <ClipboardPlus className="w-4 h-4" /> Done: log visit
            </Link>
          )}
          {audience === "public" && (
            <Link to="/register?ref=demo-scenarios" data-testid="demo-public-cta"
                  className="inline-flex items-center gap-1 text-sm px-3 py-2 rounded-full bg-[#1B4332] text-white font-medium">
              Try ConfirmPH for my business →
            </Link>
          )}
        </div>
      )}

      {/* Bottom action buttons: hidden when embedded */}
      {!embedded && (
        <div className="mt-6 flex items-center justify-between gap-2 text-xs text-slate-500">
          <div className="flex items-center gap-1">
            {customerConfirmed && <span className="rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[10px] font-semibold">Customer confirmed in demo</span>}
          </div>
          <button onClick={chooseAnother}
                  data-testid="demo-choose-another-bottom"
                  className="text-emerald-800 underline underline-offset-2">
            Choose another scenario
          </button>
        </div>
      )}
    </div>
  );
}

// Business-owner-facing explainer for the public /demo-business/scenarios
// experience.  Strict rules: no sales-agent language, only product-truth
// wording, kept short so the scenario itself does the teaching.
function OwnerExplainer({ explainer }) {
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4"
         data-testid="demo-owner-explainer">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
            What you&apos;re seeing
          </div>
          <p className="mt-1 text-sm text-slate-800 leading-snug">{explainer.what}</p>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
            Why it helps
          </div>
          <p className="mt-1 text-sm text-slate-800 leading-snug">{explainer.why}</p>
        </div>
      </div>
      {explainer.tryIt && (
        <div className="mt-3 border-t border-slate-200 pt-3">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Try it</div>
          <p className="mt-1 text-sm text-slate-700">{explainer.tryIt}</p>
        </div>
      )}
    </div>
  );
}

function ManagerViewToggle({ managerView, setManagerView }) {
  return (
    <button onClick={() => setManagerView(v => !v)}
            data-testid="demo-manager-view-toggle"
            className={`inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full border ${managerView ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"}`}
            title={managerView ? "Exit Manager View" : "Present to Manager"}>
      {managerView ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
      {managerView ? "Exit manager view" : "Present to manager"}
    </button>
  );
}

// Compact segmented control that flips the Business-side surface between
// desktop (office computer) and phone (staff mobile PWA).  Persists via the
// same sessionStorage bag the rest of the demo state uses.  Scenario state
// (bookings, warnings, current step) survives the switch untouched.
function DeviceToggle({ mode, setMode }) {
  return (
    <div className="inline-flex items-center rounded-full border border-slate-300 bg-white p-0.5"
         data-testid="demo-device-toggle" role="group" aria-label="Business-side device">
      <button onClick={() => setMode("desktop")}
              data-testid="demo-device-desktop"
              className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full transition-colors ${mode === "desktop" ? "bg-[#1B4332] text-white" : "text-slate-600 hover:bg-slate-100"}`}
              title="Show the business-side surface as the office computer">
        <Monitor className="w-3.5 h-3.5" /> Desktop
      </button>
      <button onClick={() => setMode("phone")}
              data-testid="demo-device-phone"
              className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full transition-colors ${mode === "phone" ? "bg-[#1B4332] text-white" : "text-slate-600 hover:bg-slate-100"}`}
              title="Show the business-side surface as the staff mobile PWA">
        <Smartphone className="w-3.5 h-3.5" /> Phone
      </button>
    </div>
  );
}

function AutoPlayControls({ autoPlay, setAutoPlay, onBack, onNext, onReset }) {
  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-1 py-1"
         data-testid="demo-auto-controls">
      <button onClick={onReset} data-testid="demo-auto-restart"
              className="p-1.5 rounded-full hover:bg-slate-100" title="Restart">
        <SkipBack className="w-3.5 h-3.5 text-slate-700" />
      </button>
      <button onClick={onBack} className="p-1.5 rounded-full hover:bg-slate-100" title="Previous">
        <ChevronLeft className="w-3.5 h-3.5 text-slate-700" />
      </button>
      <button onClick={() => setAutoPlay(v => !v)}
              data-testid="demo-auto-play-toggle"
              className={`p-1.5 rounded-full ${autoPlay ? "bg-[#1B4332] text-white" : "hover:bg-slate-100 text-slate-700"}`}
              title={autoPlay ? "Pause auto-play" : "Auto play"}>
        {autoPlay ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
      </button>
      <button onClick={onNext} className="p-1.5 rounded-full hover:bg-slate-100" title="Next">
        <ChevronRight className="w-3.5 h-3.5 text-slate-700" />
      </button>
      <button onClick={() => { setAutoPlay(false); onNext(); }}
              className="p-1.5 rounded-full hover:bg-slate-100" title="Skip forward">
        <SkipForward className="w-3.5 h-3.5 text-slate-700" />
      </button>
    </div>
  );
}

function renderPanel(p, { onCustomerConfirm }) {
  if (!p) return null;
  const { key: _panelKey, kind, ...rest } = p;
  switch (kind) {
    case "chat-focus":            return <ChatFocusPanel {...rest} />;
    case "notepad":               return <NotepadPanel {...rest} />;
    case "draft":                 return <DraftPanel {...rest} />;
    case "booking":               return <BookingPanel {...rest} />;
    case "confirmation-sent":     return <ConfirmationSentPanel {...rest} />;
    case "customer-confirmation": return <CustomerConfirmationPanel {...rest} onConfirm={onCustomerConfirm} />;
    case "confirmed":             return <ConfirmedPanel {...rest} />;
    case "vehicle":               return <VehiclePanel {...rest} />;
    case "conflict":              return <ConflictPanel {...rest} />;
    case "warning":               return <WarningPanel {...rest} />;
    case "follow-up":             return <FollowUpPanel {...rest} />;
    case "incident":              return <IncidentPanel {...rest} />;
    case "activity":              return <ActivityPanel {...rest} />;
    case "handover":              return <HandoverPanel {...rest} />;
    default:                       return null;
  }
}
